/**
 * Intended-plan capture for the account-first subscription funnel.
 *
 * Marketing landing pages (coach.viveka.world: first-100, early-adopters,
 * join-network) send coaches to the marketplace subscription page with the
 * plan they picked encoded in the URL:
 *   https://market.viveka.world/account/payments/withdrawal?plan=lifetime&period=annual
 *
 * Because that page is behind auth, an unauthenticated coach is bounced
 * through login/signup first — which would normally DROP the plan they
 * chose. We stash the intent here on client bootstrap (it survives the
 * auth round-trip), and StripePayoutPage resumes it: preselects the
 * billing period and opens the matching Chargebee checkout automatically,
 * so the coach lands straight on the plan they clicked instead of having
 * to re-find it. This is what keeps people from getting lost between "I
 * want this plan" and "where do I pay / create my profile".
 *
 * We store a tier alias (network | verified | lifetime) + period, NOT a
 * concrete Chargebee plan ID — StripePayoutPage owns the final plan-ID
 * resolution (First Mover vs standard SKU, monthly vs annual), which
 * depends on the coach's eligibility and can't be known by a static page.
 *
 * Mirrors util/referral.js (same localStorage-with-TTL pattern), but with
 * a short TTL — plan intent is a single funnel hop, not a 30-day window.
 */

const STORAGE_KEY = 'viveka_intended_plan';
const TTL_MS = 24 * 60 * 60 * 1000; // 1 day

const VALID_PERIODS = ['monthly', 'annual'];

/**
 * Normalize a raw ?plan= value to a tier alias. Forgiving on purpose:
 * accepts the aliases (network/verified/lifetime) AND full Chargebee plan
 * IDs (e.g. Legacy-Subscriber---Lifetime, Verified-Coach---Annual) by
 * substring, so a landing page can pass either. Returns null if unknown.
 */
const normalizePlan = raw => {
  if (!raw) return null;
  const s = String(raw).toLowerCase();
  if (s.indexOf('lifetime') !== -1) return 'lifetime';
  if (s.indexOf('verified') !== -1) return 'verified';
  if (s.indexOf('network') !== -1) return 'network';
  return null;
};

/**
 * Resolve the billing period from explicit ?period= first, then fall back
 * to inferring 'annual' from an annual plan ID. Defaults to 'monthly'.
 * (Lifetime has no period; StripePayoutPage ignores it for lifetime.)
 */
const normalizePeriod = (rawPlan, rawPeriod) => {
  const p = String(rawPeriod || '').toLowerCase();
  if (VALID_PERIODS.indexOf(p) !== -1) return p;
  if (String(rawPlan || '').toLowerCase().indexOf('annual') !== -1) return 'annual';
  return 'monthly';
};

/**
 * Read ?plan= (and optional ?period=) from the current URL and persist it.
 * Call once on client bootstrap. Safe in any environment (no-ops without
 * window). Does nothing if there's no recognizable plan in the URL, so it
 * never clobbers a previously stored intent on unrelated navigations.
 */
export const captureIntendedPlan = () => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const params = new URLSearchParams(window.location.search);
    // Accept either landing-page convention: first-100.html uses
    // ?plan=<tier>&period=<…>; early-adopters.html uses a combined
    // ?intent=<tier>-<period> (e.g. network-monthly, verified-annual,
    // lifetime). normalizePlan/normalizePeriod handle both by substring.
    const rawPlan = params.get('plan') || params.get('intent');
    const plan = normalizePlan(rawPlan);
    if (!plan) return;
    const period = normalizePeriod(rawPlan, params.get('period'));
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ plan, period, at: Date.now() })
    );
  } catch (e) {
    // localStorage can throw in private mode — capture is best-effort.
  }
};

/**
 * The stored intent { plan, period }, or null if absent/expired/invalid.
 */
export const getIntendedPlan = () => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { plan, period, at } = JSON.parse(raw);
    if (!plan || !at || Date.now() - at > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return { plan, period: period || 'monthly' };
  } catch (e) {
    return null;
  }
};

/** Clear the stored intent (after it's been resumed, or on subscribe). */
export const clearIntendedPlan = () => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
};
